import type {
  DrillScopeArgs,
  DrillScopeResult,
  InsightsMeta,
} from "src/pages/ad-studio/insights/performance/types";
import { useInsightsMeta } from "src/pages/ad-studio/insights/performance/use-insights-meta";

const toDrillScope = ({
  meta,
  args,
}: {
  meta: InsightsMeta | "no-connected-channels";
  args: DrillScopeArgs;
}): DrillScopeResult => {
  switch (meta) {
    case "no-connected-channels": {
      return { status: "failed", failure: { cause: "sync-not-connected" } };
    }
    default: {
      return toConnectedScope({ meta, args });
    }
  }
};

const toConnectedScope = ({
  meta,
  args,
}: {
  meta: InsightsMeta;
  args: DrillScopeArgs;
}): DrillScopeResult => {
  const sync = meta.syncs.find(({ id }) => id === args.sync.syncId);
  if (sync === undefined) {
    return { status: "failed", failure: { cause: "sync-not-connected" } };
  }
  return {
    status: "loaded",
    value: {
      meta,
      query: {
        scope: [
          {
            channel: sync.channel,
            syncs: [args.sync],
          },
        ],
        conversionEvents:
          args.conversionEvents === "meta-defaults"
            ? meta.defaultEvents
            : args.conversionEvents,
        dates: args.dates,
      },
      list: args.list,
    },
  };
};

export function useDrillScope(args: DrillScopeArgs): DrillScopeResult {
  const meta = useInsightsMeta();
  switch (meta.status) {
    case "loading":
    case "failed": {
      return meta;
    }
    case "loaded": {
      return toDrillScope({ meta: meta.value, args });
    }
    default: {
      return meta satisfies never;
    }
  }
}
